import { inject as service } from '@ember/service';
import { equal, or } from '@ember/object/computed';
import Controller from '@ember/controller';
import { action, computed } from '@ember/object';
import { dropTask, task } from 'ember-concurrency';
import ignorePromiseFailure from 'cio/utils/ignore-promise-failure';
import ApiService from 'cio/services/api';
import ApiErrorsService from 'cio/services/api-errors';
import AuthService from 'cio/services/auth';
import AnalyticsService from 'cio/services/analytics';
import SegmentAuthService from 'cio/services/segment-auth';
import EnvironmentService from 'cio/services/environment';
import AccountService from 'cio/services/account';
import { tracked } from '@glimmer/tracking';
import IntegrationsSegmentOutModel from 'cio/models/integrations/segment-out';
import { assertExists } from 'cio/utils/assertions';
import AlertsService from 'cio/services/alerts';
import { isNone, isPresent } from '@ember/utils';
import RouterService from '@ember/routing/router-service';
import { SEGMENT_OUT_META } from 'cio/utils/integrations/segment-out';
import DS from 'ember-data';
import cloneDeep from 'lodash.clonedeep';

export default class IntegrationsSegmentOutController extends Controller {
  @service account!: AccountService;
  @service api!: ApiService;
  @service apiErrors!: ApiErrorsService;
  @service auth!: AuthService;
  @service alerts!: AlertsService;
  @service analytics!: AnalyticsService;
  @service segmentAuth!: SegmentAuthService;
  @service environment!: EnvironmentService;
  @service router!: RouterService;
  @service store!: DS.Store;

  @tracked integration?: IntegrationsSegmentOutModel;
  @tracked isLegacySegmentOutEnabled = false;

  segmentImage = new URL('cio/assets/images/integrations/segment.svg', import.meta.url).toString();

  resetDefaults() {
    this.integration?.rollbackAttributes();
  }

  get isConnected() {
    return !isNone(this.integration);
  }

  get isLegacySegmentIntegration() {
    return !this.integration?.use_javascript;
  }

  get isNewIntegration() {
    // originally empty events list is only possible for new integration
    // later save is not allowed if events list is empty, you can only disable / remove the integration

    let isEventsAlreadyAdded = this.integration?.events && this.integration.events.length > 0;

    // attributes changed
    if (isPresent(this.integration?.changedAttributes().events)) {
      let originalEvents = this.integration?.changedAttributes().events?.[0];
      isEventsAlreadyAdded = originalEvents && originalEvents.length > 0;
    }

    return !isEventsAlreadyAdded;
  }

  get shouldEnableIntegrationWhenSave(): boolean {
    // if the integration is new and disabled allow enabling it
    return this.isNewIntegration && !this.integration?.enabled;
  }

  get canAddIntegration() {
    return this.auth.hasPermission('journeys/segment_integration/create');
  }

  get canUpdateIntegration() {
    return this.auth.hasPermission('journeys/segment_integration/edit');
  }

  get canSaveIntegration() {
    return this.integration?.hasDirtyAttributes && this.integration.events?.length > 0 && this.canUpdateIntegration;
  }

  fetchIntegrationTask = task(async () => {
    try {
      this.integration = await this.store.queryRecord('integrations/segment-out', {});
      if (this.isLegacySegmentIntegration) {
        this.isLegacySegmentOutEnabled = await this.fetchIntegration.perform();
      }
    } catch (e) {
      // When there is no integration, it will return a 404. That is an acceptable error,
      // but we should throw on all other error types.
      if (e.errors[0].status !== '404') {
        throw e;
      }

      // status is 404, so check for legacy integration if present
      this.isLegacySegmentOutEnabled = await this.fetchIntegration.perform();
    } finally {
      if (this.isLegacySegmentOutEnabled) {
        this.integration = undefined;
      }
    }
  });

  connectIntegrationTask = task(async (key: string) => {
    let integration = this.store.createRecord('integrations/segment-out', {
      key,
      use_javascript: true,
    });
    try {
      await integration.save();
      this.integration = integration;
    } catch (e) {
      this.apiErrors.report(e);
      integration.rollbackAttributes();
    }
  });

  disconnectIntegrationTask = task(async () => {
    assertExists(this.integration);

    try {
      await this.integration.destroyRecord();
      this.alerts.success('segment-out-delete.success', 'Segment source integration removed');
      this.integration = undefined;
    } catch {
      this.integration?.rollbackAttributes();
      this.alerts.error(
        'segment-out-delete.error',
        'Could not remove Segment source integration. Please try again.',
        'Whoops!',
      );
    }
  });

  saveIntegrationTask = task(async () => {
    assertExists(this.integration);

    if (this.shouldEnableIntegrationWhenSave) {
      this.analytics.track('integration', 'enable_started', {
        ...SEGMENT_OUT_META,
        enabledOrReenabled: this.isNewIntegration ? 'Enabled' : 'Re-Enabled',
      });
      this.integration.enabled = true;
    }

    try {
      await this.integration.save();
      this.alerts.success('segment-out.success', 'Segment source integration settings saved');
    } catch (err) {
      this.integration.rollbackAttributes();
      this.alerts.error(
        'segment-out-save.error',
        'Could not update Segment source integration. Please try again.',
        'Whoops!',
      );
    }
  });

  @action
  cancel() {
    this.integration?.rollbackAttributes();
    this.router.transitionTo('integrations');
  }

  // XXX: Legacy methods and properties
  // TODO: Cleanup once we know our users are not using legacy integration
  @tracked key: string | null = null;
  @tracked events: Record<string, boolean> = {};
  @tracked original: {
    key: IntegrationsSegmentOutController['key'];
    events: IntegrationsSegmentOutController['events'];
  } = {
    key: null,
    events: {},
  };

  @computed('events')
  get eventNames() {
    return Object.keys(this.events || {}).sort();
  }

  @computed('events', 'original.events')
  get isDirty() {
    let events = this.events || {};
    let original = this.original.events || {};

    return Object.keys(original).some((n) => events[n] !== original[n]);
  }

  fetchIntegration = dropTask(async () => {
    let request = this.api.request('integrations/segdotcom', {
      ignoreErrors: true,
      includeStatus: true,
    });

    try {
      let response = await request;

      let { key, events } = response;
      this.key = key;
      this.events = events;
      this.original = { key, events };

      return true;
    } catch (e) {
      if (e.status === '404') {
        this.key = null;
        this.events = {};
        this.original = { key: null, events: {} };

        return false;
      }

      this.apiErrors.report(e);
    }

    return false;
  });

  addIntegration = dropTask(async () => {
    // Any new integration created from legacy form, will create a new Segment JS integration
    if (this.key) {
      this.connectIntegrationTask.perform(this.key);
    }
  });

  removeIntegration = dropTask(async () => {
    try {
      await this.api.delete('integrations/segdotcom');
      this.integration = undefined;

      this.key = null;
      this.events = {};
      this.original = { key: null, events: {} };
      this.isLegacySegmentOutEnabled = false;

      this.alerts.success('segment-out-delete.success', 'Segment source integration removed');
    } catch {
      this.alerts.error(
        'segment-out-delete.error',
        'Could not remove Segment source integration. Please try again.',
        'Whoops!',
      );
    }

    await this.fetchIntegrationTask.perform();
  });

  @equal('fetchIntegration.last.value', true) hasIntegration!: boolean;
  @equal('fetchIntegration.last.value', false) hasNoIntegration!: boolean;
  @or('fetchIntegration.isRunning', 'hasIntegration', 'hasNoIntegration') noError!: boolean;

  @action
  reset() {
    this.key = this.original.key;
    this.events = cloneDeep(this.original.events);
  }

  @action
  save() {
    let data = { key: this.key, events: this.events };
    let promise = this.api.put('integrations/segdotcom', { data });

    promise.then(() => (this.original = data), ignorePromiseFailure);

    return promise;
  }

  @action
  updateEvent(name: string, checked: boolean) {
    let events: Record<string, boolean> = {
      ...this.events,
    };

    events[name] = checked;

    this.events = events;
  }

  @action
  integrationLinkClicked() {
    let trackData = {
      from: 'In-App',
      name: 'Segment',
      category: 'Data management',
      enabled: !!this.segmentAuth.segmentIn.last?.value,
      type: 'Native',
      subtitle: 'Destination',
    };

    this.analytics.track('integration', 'clicked', trackData);
  }
}
